/**
 * PanelDrawingService — 폴리곤 분할 및 좌표 변환 로직
 *
 * Pure JS: Vue 의존성 없음. 모든 상태/API/좌표변환은 context 파라미터로 주입.
 */
import siteDesignApi from '../../../apis/panel/site_design_api.js';
import { stringFromApi, stringToApi } from '../utils/panelHierarchyMappers.js';
import { closeRingCoordinates } from '../utils/closeRingCoordinates.js';
import { moduleColRowToApiFields } from '../utils/panelDivisionGrid.js';
import {
  getModuleCellCornersForDivide,
  getQuadCorners
} from '../utils/getModuleCellCorners.js';
import { getWidthOrHeight } from '../utils/panelEdgeOrientation.js';
import {
  logStringNumberingInArrayDebug,
  resolveNextStringDisplayIdInArray,
  resolveStringCreateTargetArraySeq
} from '../utils/panelEntityNumbering.js';
import { createStringCreatedPatch } from '../utils/panelSiteDesignPatch.js';
import {
  logStringCopySave,
  warnStringCopySave,
  errorStringCopySave,
  summarizeRawApiResult,
  traceStringCopySaveAsync
} from '../utils/stringCopySaveDebug.js';
import { resolveStringCopySkipGridSlots } from '../utils/panelStringCopySkipSlots.js';

function toArraySeqFromNode(node) {
  if (node?.solarArraySeq != null) return Number(node.solarArraySeq);
  const match = String(node?.id || '').match(/^array_(\d+)$/);
  return match ? Number(match[1]) : null;
}

function findArrayNodeBySeq(nodes, targetSeq) {
  for (const node of nodes || []) {
    if (node.type === 'array') {
      const seq = toArraySeqFromNode(node);
      if (seq != null && Number(seq) === Number(targetSeq)) {
        return node;
      }
    }
    const inChild = findArrayNodeBySeq(node.children, targetSeq);
    if (inChild) return inChild;
  }
  return null;
}

export function createPanelDrawingService() {
  return {
    /* ───── pure math ───── */

    /**
     * 선형 보간
     * @param {[number,number]} p1
     * @param {[number,number]} p2
     * @param {number} t 0~1
     * @returns {[number,number]}
     */
    interpolate(p1, p2, t) {
      return [p1[0] + (p2[0] - p1[0]) * t, p1[1] + (p2[1] - p1[1]) * t];
    },

    /* ───── coordinate conversion ───── */

    /**
     * 맵 좌표(EPSG:4326) → API shapeCoordinates
     * convToWGS84 가 context 에 있으면 사용, 없으면 closeRingCoordinates.
     * @param {[number,number][]} coordinates
     * @param {(coords:[number,number][])=>[number,number][]} [convToWGS84]
     * @returns {[number,number][]}
     */
    convToWGS84(coordinates, convToWGS84) {
      if (!coordinates?.length) return [];
      if (typeof convToWGS84 === 'function') return convToWGS84(coordinates);
      return closeRingCoordinates(coordinates.map(c => [...c]));
    },

    /* ───── tree traversal ───── */

    /**
     * 트리에서 현재 선택된 노드의 상위 Array seq(DB PK) 조회
     * 트리 노드 id는 "array_57" 형태 문자열이므로 그대로 반환하면 안 되고,
     * 항상 숫자 seq로 변환해서 반환한다.
     * @param {Array} treeData
     * @param {*} focusedNodeId
     * @returns {number|null}
     */
    resolveCurrentArraySeq(treeData, focusedNodeId) {
      const tree = treeData || [];
      const focusId = focusedNodeId;
      if (!focusId || !tree.length) return null;

      const findArraySeq = (nodes, parentArraySeq) => {
        for (const node of nodes) {
          if (node.id === focusId) {
            if (node.type === 'array') return toArraySeqFromNode(node);
            return parentArraySeq;
          }
          if (node.children?.length) {
            const nextParentSeq = node.type === 'array' ? toArraySeqFromNode(node) : parentArraySeq;
            const found = findArraySeq(node.children, nextParentSeq);
            if (found != null) return found;
          }
        }
        return null;
      };

      return findArraySeq(tree, null);
    },

    /**
     * focusedNodeId 기준 대상 Array 트리 노드
     * @returns {object|null}
     */
    resolveCurrentArrayNode(treeData, focusedNodeId) {
      const seq = this.resolveCurrentArraySeq(treeData, focusedNodeId);
      if (seq == null) return null;
      return findArrayNodeBySeq(treeData, seq);
    },

    /* ───── API-driven operations ───── */

    /**
     * 폴리곤 영역을 모듈 그리드로 분할하고 API로 저장
     *
     * @param {object} ctx
     * @param {{ deferRefresh?: boolean, solarArraySeq?: number|null }} [options]
     *   options.solarArraySeq — 드래그 복사 등에서 원본 String의 Array seq를 상속할 때 사용.
     *   지정되면 트리 포커스 기반 조회보다 우선한다.
     */
    async dividePolygon(ctx, options = {}) {
      const { deferRefresh = false, solarArraySeq: solarArraySeqOverride = null } = options;
      const skipGridSlots =
        options.skipGridSlots ??
        ctx.skipGridSlots ??
        (ctx._ctrlDragCopySourceFeature
          ? resolveStringCopySkipGridSlots({
              sourceStringFeature: ctx._ctrlDragCopySourceFeature,
              moduleRowNum: ctx.moduleRowNum,
              moduleColNum: ctx.moduleColNum,
              moduleNumberingOrientation: ctx.moduleNumberingOrientation,
              mapFeatures: ctx.vectorSource?.getFeatures?.() ?? []
            })
          : null);
      logStringCopySave('dividePolygon → enter', {
        deferRefresh,
        solarArraySeqOverride,
        hasDrawnPolygon: Boolean(ctx.drawnPolygon),
        moduleColNum: ctx.moduleColNum,
        moduleRowNum: ctx.moduleRowNum,
        siteId: ctx.selectedSite?.id ?? null,
        ctrlDragCopy: Boolean(ctx._ctrlDragCopySourceFeature),
        skipGridSlotCount: skipGridSlots?.size ?? 0
      });

      if (!ctx.selectedSite?.id) {
        errorStringCopySave('dividePolygon → abort (no site)');
        return;
      }
      if (!ctx.drawnPolygon || ctx.moduleColNum < 1 || ctx.moduleRowNum < 1) {
        warnStringCopySave('dividePolygon → early return (drawnPolygon/row/col)', {
          hasDrawnPolygon: Boolean(ctx.drawnPolygon),
          moduleColNum: ctx.moduleColNum,
          moduleRowNum: ctx.moduleRowNum
        });
        return;
      }
      const geom = ctx.drawnPolygon.getGeometry();
      const coords = geom.getCoordinates()[0].map(([x, y]) => [x, y]);
      const corners = getQuadCorners(coords);
      if (corners.length < 4) {
        console.error('dividePolygon: 꼭짓점 4개가 필요합니다.');
        return;
      }

      const currentArraySeq = resolveStringCreateTargetArraySeq({
        solarArraySeqOverride,
        lockedStringDrawTargetArraySeq: ctx.lockedStringDrawTargetArraySeq,
        stringDrawTargetArraySeq: ctx.stringDrawTargetArraySeq,
        treeData: ctx.treeData,
        focusedNodeId: ctx.focusedNodeId,
        resolveCurrentArraySeq: (treeData, focusedNodeId) =>
          this.resolveCurrentArraySeq(treeData, focusedNodeId)
      });

      if (currentArraySeq == null) {
        warnStringCopySave('dividePolygon → abort (no target array seq)');
        return null;
      }

      if (solarArraySeqOverride != null || ctx._ctrlDragCopyDebug) {
        logStringNumberingInArrayDebug(
          {
            flattened: ctx.flattenedSiteDesign,
            solarStrings: ctx.solarStrings,
            arraySeq: currentArraySeq,
            sourceFeature: ctx._ctrlDragCopySourceFeature ?? null
          },
          solarArraySeqOverride != null ? 'CtrlDragCopy → dividePolygon' : 'dividePolygon'
        );
      }

      const solarStringIndex = resolveNextStringDisplayIdInArray({
        flattened: ctx.flattenedSiteDesign,
        solarStrings: ctx.solarStrings,
        arraySeq: currentArraySeq
      });

      const { rowNum, colNum } = moduleColRowToApiFields(ctx.moduleColNum, ctx.moduleRowNum);

      const solarString = {
        stringId: solarStringIndex,
        name: `String ${solarStringIndex}`,
        shapeCoordinates: this.convToWGS84(coords, ctx.convToWGS84),
        rowNum,
        colNum,
        solarModules: [],
        // 백엔드 SolarString 엔티티의 @Transient solarArrayId에 매핑되는 필드명 (값은 Array seq)
        solarArrayId: currentArraySeq
      };

      const [arr1, arr2, arr3, arr4] = corners;
      const firstSide = getWidthOrHeight(arr1, arr2);
      const secondSide = getWidthOrHeight(arr2, arr3);

      const moduleWidth =
        firstSide.type === 'width'
          ? firstSide.distance / ctx.moduleColNum
          : secondSide.distance / ctx.moduleColNum;
      const moduleHeight =
        firstSide.type === 'width'
          ? secondSide.distance / ctx.moduleRowNum
          : firstSide.distance / ctx.moduleRowNum;

      ctx.moduleWidth = moduleWidth;
      ctx.moduleHeight = moduleHeight;

      for (let i = 0; i < ctx.moduleRowNum; i++) {
        for (let j = 0; j < ctx.moduleColNum; j++) {
          // moduleNumberingOrientation: 'row-major'(가로 기준) | 'col-major'(세로 기준)
          const orientation = ctx.moduleNumberingOrientation || 'row-major';
          const localIndex =
            orientation === 'col-major'
              ? j * ctx.moduleRowNum + i + 1 // 세로 기준: 열 우선
              : i * ctx.moduleColNum + j + 1; // 가로 기준: 행 우선 (기본값)

          if (skipGridSlots?.has(localIndex)) {
            continue;
          }

          // moduleId는 String 내부 1-based (사이트 전역 maxModuleId와 무관)
          const moduleIndex = localIndex;

          const moduleRing = getModuleCellCornersForDivide(
            arr1, arr2, arr3, arr4,
            ctx.moduleRowNum, ctx.moduleColNum,
            i, j
          );
          solarString.solarModules.push({
            moduleId: moduleIndex,
            shapeCoordinates: this.convToWGS84(moduleRing, ctx.convToWGS84)
          });
        }
      }

      try {
        const result = await traceStringCopySaveAsync(
          'addSolarStringAndItsModules (dividePolygon)',
          () => siteDesignApi.addSolarStringAndItsModules(ctx.selectedSite.id, solarString),
          {
            siteId: ctx.selectedSite.id,
            stringDisplayId: solarStringIndex,
            arraySeq: currentArraySeq,
            moduleCount: solarString.solarModules.length,
            skipGridSlotCount: skipGridSlots?.size ?? 0
          }
        );

        logStringCopySave('dividePolygon → API raw summary', summarizeRawApiResult(result));

        if (solarArraySeqOverride != null) {
          logStringCopySave('[CtrlDragCopy] dividePolygon → API result', {
            targetArraySeq: currentArraySeq,
            newStringDisplayId: solarStringIndex,
            newStringName: solarString.name,
            ...summarizeRawApiResult(result)
          });
        }

        if (ctx.vectorSource && ctx.drawnPolygon) {
          ctx.vectorSource.removeFeature(ctx.drawnPolygon);
        }
        ctx.drawnPolygon = null;

        const patch = createStringCreatedPatch(result, solarString, currentArraySeq);
        let applied = false;
        if (typeof ctx.applyStringCreatedPatch === 'function') {
          applied = Boolean(
            await traceStringCopySaveAsync(
              'applyStringCreatedPatch (from dividePolygon)',
              () => ctx.applyStringCreatedPatch(patch),
              { arraySeq: currentArraySeq }
            )
          );
        } else if (!deferRefresh) {
          await traceStringCopySaveAsync(
            'syncLocalStateAfterStringChanges (no patch fn)',
            () => this.syncLocalStateAfterStringChanges(ctx)
          );
          applied = true;
        } else {
          warnStringCopySave('dividePolygon → deferRefresh, patch fn 없음 — applied=false');
        }

        logStringCopySave('dividePolygon → exit', { applied, deferRefresh });
        return applied ? patch : null;
      } catch (e) {
        errorStringCopySave('dividePolygon → catch', {
          message: e?.message,
          stack: e?.stack
        });
      }
    },

    /**
     * String 변경 후 로컬 flattened·맵 index만 동기화 (getSiteDesign / 전체 트리·맵 rebuild 없음)
     *
     * @param {object} ctx
     */
    async syncLocalStateAfterStringChanges(ctx) {
      logStringCopySave('syncLocalStateAfterStringChanges → start', {
        siteId: ctx.selectedSite?.id ?? null
      });
      const t0 = performance.now();

      if (typeof ctx.syncPanelPlanningLocalStateFromFlattened === 'function') {
        ctx.syncPanelPlanningLocalStateFromFlattened();
      } else if (typeof ctx.syncFlattenedToStore === 'function') {
        ctx.syncFlattenedToStore({ force: true });
      }

      logStringCopySave('syncLocalStateAfterStringChanges → done', {
        elapsedMs: (performance.now() - t0).toFixed(1),
        solarStringCount: ctx.solarStrings?.length ?? null
      });
    },

    /** @deprecated syncLocalStateAfterStringChanges 사용 */
    async refreshSiteDesignAfterStringChanges(ctx) {
      return this.syncLocalStateAfterStringChanges(ctx);
    }
  };
}
